import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Building2, Factory, Home, Hammer, Sparkles, SprayCan,
  CheckCircle, ArrowRight, Clock, Shield, Users
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const ServicesPage = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Building2,
      title: t('nettoyageBureauxTitle'),
      description: t('nettoyageBureauxDesc'),
      slug: "nettoyage-bureaux",
      features: [
        t('serviceFeature1_1'),
        t('serviceFeature1_2'),
        t('serviceFeature1_3'),
        t('serviceFeature1_4'),
        t('serviceFeature1_5')
      ],
      image: "https://images.unsplash.com/photo-1637095937545-7d8c1edf4d2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBjbGVhbmluZyUyMHByb2Zlc3Npb25hbCUyMGJyaWdodHxlbnwwfHx8fDE3NzIxMzc4MTZ8MA&ixlib=rb-4.1.0&q=85",
      color: "bg-blue-600"
    },
    {
      icon: Factory,
      title: t('nettoyageIndustrielFullTitle'),
      description: t('nettoyageIndustrielFullDesc'),
      slug: "nettoyage-industriel",
      features: [
        t('serviceFeature2_1'),
        t('serviceFeature2_2'),
        t('serviceFeature2_3'),
        t('serviceFeature2_4'),
        t('serviceFeature2_5')
      ],
      image: "https://images.pexels.com/photos/5759145/pexels-photo-5759145.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      color: "bg-slate-700"
    },
    {
      icon: Home,
      title: t('entretienImmeublesTitle'),
      description: t('entretienImmeublesDesc'),
      slug: "entretien-immeubles",
      features: [
        t('serviceFeature3_1'),
        t('serviceFeature3_2'),
        t('serviceFeature3_3'),
        t('serviceFeature3_4'),
        t('serviceFeature3_5')
      ],
      image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
      color: "bg-emerald-600"
    },
    {
      icon: Hammer,
      title: t('nettoyageApresTravauxTitle'),
      description: t('nettoyageApresTravauxDesc'),
      slug: "nettoyage-apres-travaux",
      features: [
        t('serviceFeature4_1'),
        t('serviceFeature4_2'),
        t('serviceFeature4_3'),
        t('serviceFeature4_4'),
        t('serviceFeature4_5')
      ],
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800",
      color: "bg-amber-600"
    },
    {
      icon: Sparkles,
      title: t('vitresSurfacesFullTitle'),
      description: t('vitresSurfacesFullDesc'),
      slug: "nettoyage-vitres",
      features: [
        t('serviceFeature5_1'),
        t('serviceFeature5_2'),
        t('serviceFeature5_3'),
        t('serviceFeature5_4'),
        t('serviceFeature5_5')
      ],
      image: "https://images.unsplash.com/photo-1482449609509-eae2a7ea42b7",
      color: "bg-sky-600"
    },
    {
      icon: SprayCan,
      title: t('servicesSpecifiquesTitle'),
      description: t('servicesSpecifiquesDesc'),
      slug: "desinfection",
      features: [
        t('serviceFeature6_1'),
        t('serviceFeature6_2'),
        t('serviceFeature6_3'),
        t('serviceFeature6_4'),
        t('serviceFeature6_5')
      ],
      image: "https://images.pexels.com/photos/6812463/pexels-photo-6812463.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      color: "bg-teal-600"
    }
  ];

  const benefits = [
    { icon: Clock, title: t('disponibilite247'), description: t('disponibilite247Desc') },
    { icon: Shield, title: t('qualiteGarantie'), description: t('qualiteGarantieDesc') },
    { icon: Users, title: t('equipesFormees'), description: t('equipesFormeesDesc') }
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      <SEO
        title="Nos services de nettoyage professionnel"
        description="Découvrez tous nos services de nettoyage : bureaux, industries, copropriétés, nettoyage après travaux, désinfection. Équipes qualifiées, produits écologiques, devis gratuit en Belgique."
        keywords="services nettoyage, nettoyage bureaux, nettoyage industriel, entretien immeubles, nettoyage après travaux, désinfection professionnelle Belgique"
        canonicalUrl="https://www.nettis.be/services"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('nosExpertises')}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
              {t('servicesPageTitle')} <span className="text-[#1E3A8A]">{t('professionnel')}</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">{t('servicesPageDesc')}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="flex items-center gap-4 bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
                  <benefit.icon className="w-6 h-6 text-[#1E3A8A]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{benefit.title}</h3>
                  <p className="text-sm text-slate-500">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div key={service.title} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${service.color} text-white mb-6`}>
                    <service.icon className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">{service.title}</h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button className="bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full px-6 font-semibold shadow-lg shadow-blue-900/20 transition-transform duration-200 hover:-translate-y-0.5">
                      {t('nousContacter')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/services/${service.slug}`} className="ml-4">
                    <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5 rounded-full px-6">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img src={service.image} alt={service.title} className="w-full h-80 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('besoinSpecifique')}</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t('besoinSpecifiqueDesc')}</p>
          <Link to="/contact">
            <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-green-900/20 transition-transform duration-200 hover:-translate-y-0.5">
              {t('nousContacter')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
